import { useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import { isEmpty, compose, sortWith, ascend, uniq } from 'ramda';
import { useSelector } from 'react-redux';
import { getAPI } from '../../services/api';
import {
  QUERY_KEY_BONUSES,
  QUERY_KEY_BONUS_HISTORY,
  QUERY_KEY_BONUS_CONTENT,
} from '../../constants/api';
import {
  QUERY,
  ENTITY,
  PROJECT_KEY,
  CHANNEL_CONFIG_MAP,
} from '../../constants/content';
import {
  COLLECTING_BONUS_MECHANIC,
  PENDING,
  PENDING_BEFORE_WAGERING,
  WAGERING_BONUS_MECHANIC,
} from '../../constants/bonuses';
import {
  NOT_AVAILABLE,
  WAGERING,
  COLLECTING,
  CASHBACK_BONUS_TYPE,
} from '../../constants/bonuses';
import {
  getBonusContentEntity,
  getUIEntityFromBonus,
  getUIEntityFromCampaign,
} from '../../utils/bonus';
import { getEntityWithImageUrl } from '../../utils/imagePath';
import { isLoggedInSelector } from '../../redux/reducers/app/params';
import { contentAPIPrefixSelector } from '../../redux/reducers/app/configuration';
import { useBonusesSelector } from '../../redux/reducers/app/configuration';
import { useCurrentBonuses } from './useCurrentBonuses';
import { useCommonQueryParams } from './useCommonQueryParams';
import useRequestConfig from './useRequestConfig';
import FEATURES from '../../constants/featureToggling';
import { useFeature } from '@growthbook/growthbook-react';
import { sdkErrorHandler } from '../../utils';
import { useIsUnifiedPromoBonusSystemEnabled } from '../useIsUnifiedPromoBonusSystemEnabled';

export function mapWageringBonuses(bonuses) {
  return (
    bonuses?.map?.((bonus) => ({
      ...bonus,
      status: bonus.status === PENDING ? PENDING_BEFORE_WAGERING : bonus.status,
      mechanicType: WAGERING_BONUS_MECHANIC,
    })) ?? []
  );
}

export function mapCollectingBonuses(bonuses) {
  return (
    bonuses?.map?.((bonus) => ({
      ...bonus,
      mechanicType: COLLECTING_BONUS_MECHANIC,
    })) ?? []
  );
}

export const useFetchBonusContent = () => {
  const { language, brand, channel, contentAPIPrefix } = useCommonQueryParams();

  const query = {
    [QUERY.PROJECT]: PROJECT_KEY,
    [QUERY.LANGUAGE_ID]: language,
    [QUERY.BRANDS]: brand?.toUpperCase?.(),
    [QUERY.CHANNELS]: CHANNEL_CONFIG_MAP[channel?.toUpperCase?.()],
  };
  const searchParams = new URLSearchParams(query);

  return (ids = [], signal) => {
    if (Array.isArray(ids) && !isEmpty(ids)) {
      // use sort to cache request with CF
      const sortedIds = sortWith([ascend((a) => a)])(ids);

      sortedIds.forEach((id) => {
        if (!id || typeof id !== 'string') {
          return;
        }

        searchParams.append(QUERY.EXTERNAL_ID_IN, id);
      });
    }

    const url = `/${ENTITY.EVABONUSES}?${searchParams}`;

    return getAPI()
      .fetch(url, { signal, baseUrl: contentAPIPrefix })
      .catch(
        sdkErrorHandler({
          baseUrl: contentAPIPrefix,
          source: `fetch to ${url}`,
        })
      );
  };
};

export function buildBonusEntities(
  bonusesData = [],
  campaignsData = [],
  contentData
) {
  const bonuses = bonusesData.map((item) =>
    getUIEntityFromBonus({
      ...(contentData[item.campaignId] || {}),
      ...item,
      isCampaign: false,
    })
  );

  const campaigns = campaignsData.map((item) =>
    getUIEntityFromCampaign({
      ...(contentData[item.id] || {}),
      ...item,
      isCampaign: true,
    })
  );

  return [...bonuses, ...campaigns];
}

export const useQueryBonusSystemFn = () => {
  const requestConfig = useRequestConfig();
  const isPromoSystemEnabled = useIsUnifiedPromoBonusSystemEnabled();

  const query = {
    ...(requestConfig?.query || {}),
    ...(isPromoSystemEnabled ? { isUnifiedLobby: true } : {}),
  };

  return ({ signal }) =>
    getAPI()
      .bonuses.get({ signal, apiVersion: 'v3', ...requestConfig, query })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          apiVersion: 'v3',
          source: `SDK API bonuses.get`,
        })
      );
};

export const useBonusSystem = (queryOptions = {}) => {
  const useBonuses = useSelector(useBonusesSelector);
  const fetchBonuses = useQueryBonusSystemFn();

  return useQuery([QUERY_KEY_BONUSES], fetchBonuses, {
    staleTime: 0,
    cacheTime: 0,
    ...queryOptions,
    enabled: useBonuses,
  });
};

export const useBonusContentByIdMap = (
  ids,
  queryOptions = { enabled: true }
) => {
  const contentAPIPrefix = useSelector(contentAPIPrefixSelector);
  const fetchBonusContent = useFetchBonusContent();

  return useQuery(
    [QUERY_KEY_BONUS_CONTENT, ...ids],
    ({ signal }) => fetchBonusContent(ids, signal),
    {
      select(data) {
        return data
          .map((item) => {
            return compose(
              (entity) => getBonusContentEntity(entity, contentAPIPrefix),
              getEntityWithImageUrl
            )(item, contentAPIPrefix);
          })
          .reduce((acc, item) => ({ ...acc, [item.id]: item }), {});
      },

      ...queryOptions,
    }
  );
};

export const useQueryBonuses = (queryOptions = {}) => {
  const isDepositBonusEnabled = useFeature(FEATURES.CAN_USE_DEPOSIT_BONUS).on;

  const bonusSystemResponse = useBonusSystem(queryOptions);

  const depositCampaigns = isDepositBonusEnabled
    ? bonusSystemResponse?.data?.campaigns?.deposit ?? []
    : [];
  const cashbackCampaigns =
    bonusSystemResponse?.data?.campaigns?.cashback ?? [];

  const wageringBonusesData =
    bonusSystemResponse?.data?.bonuses?.wagering ?? [];
  const collectingBonusesData =
    bonusSystemResponse?.data?.bonuses?.collecting ?? [];

  const campaignsData = [...depositCampaigns, ...cashbackCampaigns];
  const bonusesData = [
    ...mapWageringBonuses(wageringBonusesData),
    ...mapCollectingBonuses(collectingBonusesData),
  ];

  const campaignIds =
    uniq(
      campaignsData
        ?.map((campaign) => campaign?.id)
        .concat(bonusesData.map((bonus) => bonus?.campaignId))
    ) || [];

  const isEnabledBonusContentQuery = !isEmpty(campaignIds);

  const {
    data,
    isLoading: isContentLoading,
    isSuccess: isContentSuccess,
  } = useBonusContentByIdMap(campaignIds, {
    ...queryOptions,
    enabled: isEnabledBonusContentQuery,
  });
  const contentData = data || {};

  return {
    ...bonusSystemResponse,
    isLoading: bonusSystemResponse.isLoading || isContentLoading,
    isSuccess:
      bonusSystemResponse.isSuccess &&
      (isEnabledBonusContentQuery ? isContentSuccess : true),
    data: buildBonusEntities(bonusesData, campaignsData, contentData),
  };
};

export const useQueryBonusHistory = (queryOptions = {}) => {
  const useBonuses = useSelector(useBonusesSelector);
  const isLoggedIn = useSelector(isLoggedInSelector);
  const requestConfig = useRequestConfig();
  const fetchBonusHistory = ({ signal }) =>
    getAPI()
      .bonusHistory.get({
        signal,
        apiVersion: 'v3',
        ...requestConfig,
      })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          apiVersion: 'v3',
          source: `SDK API bonusHistory.get`,
        })
      );

  const enabled =
    useBonuses &&
    isLoggedIn &&
    // eslint-disable-next-line
    (typeof queryOptions.enabled !== undefined ? queryOptions.enabled : true);

  const bonusHistoryResponse = useQuery(
    [QUERY_KEY_BONUS_HISTORY],
    fetchBonusHistory,
    {
      staleTime: 0,
      cacheTime: 0,
      ...queryOptions,
      enabled,
    }
  );

  const wageringBonusesData = bonusHistoryResponse?.data?.wagering ?? [];
  const collectingBonusesData = bonusHistoryResponse?.data?.collecting ?? [];
  const bonusesData = [
    ...mapWageringBonuses(wageringBonusesData),
    ...mapCollectingBonuses(collectingBonusesData),
  ];

  const campaignIds =
    uniq(bonusesData?.map((bonus) => bonus?.campaignId)) || [];

  const { data: contentData, isLoading: isContentLoading } =
    useBonusContentByIdMap(campaignIds, {
      ...queryOptions,
      enabled: isLoggedIn && !isEmpty(campaignIds),
    });

  const data = bonusesData?.map((item) => ({
    ...item,
    ...(contentData?.[item.campaignId] ?? {}),
    id: item.id,
    isCampaign: false,
  }));

  return {
    ...bonusHistoryResponse,
    isLoading: bonusHistoryResponse.isLoading || isContentLoading,
    data,
  };
};

export const useQueryBonusContent = (bonusId, queryOptions = {}) => {
  const query = useBonusContentByIdMap([bonusId], {
    ...queryOptions,
  });

  const bonusContent = query.data?.[bonusId] || {};
  const campaign = getUIEntityFromCampaign({
    ...bonusContent,
    status: NOT_AVAILABLE,
    isCampaign: true,
  });

  return {
    ...query,
    data: campaign,
  };
};

export const useQueryBonusDetails = (bonusId, queryOptions = {}) => {
  const bonusesResult = useQueryBonuses(queryOptions);
  const bonusesHistoryResult = useQueryBonusHistory(queryOptions);

  const availableBonuses = [
    ...bonusesResult.data,
    ...bonusesHistoryResult.data,
  ];

  const availableBonus = availableBonuses.find((item) => item.id === bonusId);
  const isEntityIsNotAvailable =
    !bonusesResult.isLoading &&
    !bonusesHistoryResult.isLoading &&
    !availableBonus;

  const queryUnavailableCampaign = useQueryUnavailableCampaign(bonusId, {
    enabled: isEntityIsNotAvailable,
  });

  const bonus = isEntityIsNotAvailable
    ? queryUnavailableCampaign.data
    : availableBonus;

  return {
    isLoading:
      bonusesResult.isLoading ||
      bonusesHistoryResult.isLoading ||
      queryUnavailableCampaign.isLoading,
    isError:
      bonusesResult.isError ||
      bonusesHistoryResult.isError ||
      queryUnavailableCampaign.isError,
    error:
      bonusesResult.error ||
      bonusesHistoryResult.error ||
      queryUnavailableCampaign.error,
    isSuccess:
      bonusesResult.isSuccess ||
      bonusesHistoryResult.isSuccess ||
      queryUnavailableCampaign.isSuccess,
    data: bonus,
  };
};

export const useMutationTakeCashback = (
  bonusId,
  campaignId,
  mutationOptions = {}
) => {
  const requestConfig = useRequestConfig();

  return useMutation(
    () =>
      getAPI()
        .bonuses.take(
          { bonusId, campaignId },
          { apiVersion: 'v3', ...requestConfig }
        )
        .catch(
          sdkErrorHandler({
            ...requestConfig,
            apiVersion: 'v3',
            bonusId,
            campaignId,
            source: `SDK API bonuses.take`,
          })
        ),
    mutationOptions
  );
};

export const useMutationJoinCampaign = (campaignId, mutationOptions = {}) => {
  const requestConfig = useRequestConfig();
  return useMutation(
    () =>
      getAPI()
        .bonuses.join(campaignId, { apiVersion: 'v3', ...requestConfig })
        .catch(
          sdkErrorHandler({
            ...requestConfig,
            apiVersion: 'v3',
            campaignId,
            source: `SDK API bonuses.join`,
          })
        ),
    mutationOptions
  );
};

export const useMutationUnJoinCampaign = (campaignId, mutationOptions = {}) => {
  const requestConfig = useRequestConfig();
  return useMutation(
    () =>
      getAPI()
        .bonuses.unjoin(campaignId, {
          apiVersion: 'v3',
          ...requestConfig,
        })
        .catch(
          sdkErrorHandler({
            ...requestConfig,
            apiVersion: 'v3',
            campaignId,
            source: `SDK API bonuses.unjoin`,
          })
        ),
    mutationOptions
  );
};

export const useMutationRejectBonus = (
  bonusId,
  campaignId,
  mutationOptions = {}
) => {
  const requestConfig = useRequestConfig();
  return useMutation(
    () =>
      getAPI()
        .bonuses.reject(
          { bonusId, campaignId },
          { apiVersion: 'v3', ...requestConfig }
        )
        .catch(
          sdkErrorHandler({
            ...requestConfig,
            apiVersion: 'v3',
            bonusId,
            campaignId,
            source: `SDK API bonuses.reject`,
          })
        ),
    mutationOptions
  );
};

export const useWageringBonus = (queryOptions = {}) => {
  const { data, ...rest } = useCurrentBonuses(queryOptions);

  return {
    data:
      data?.active?.length > 0 &&
      data?.active.find(
        (el) =>
          [PENDING_BEFORE_WAGERING, WAGERING, COLLECTING].includes(el.status) &&
          el.mechanicType === WAGERING_BONUS_MECHANIC
      ),
    ...rest,
  };
};

export const useIsWageringGamesAvailable = () => {
  const { data } = useWageringBonus();

  return useMemo(() => Boolean(data), [data]);
};

export const useCollectingGamesCampaignId = () => {
  const { data, isLoading, ...rest } = useCurrentBonuses();

  return useMemo(
    () => ({
      ...rest,
      isLoading,
      id:
        data?.active?.length > 0 &&
        data?.active.find((el) => el.status === COLLECTING)?.campaignId,
    }),
    [data, isLoading, rest]
  );
};

export const useIsActiveCashbackBonusExists = () => {
  const { data } = useQueryBonuses();

  return useMemo(
    () =>
      data.some(
        (bonus) => !bonus.isCampaign && bonus.bonusType === CASHBACK_BONUS_TYPE
      ),
    [data]
  );
};

export const useBonusesDataSelector = (selector) => {
  const { data, ...query } = useQueryBonuses();

  if (!selector) {
    throw new Error('selector is required');
  }

  return useMemo(() => {
    return selector(data, query);
  }, [data, query, selector]);
};

export const getUnavailableEntity = (entity, content) => {
  if (!content && !entity) return null;

  return {
    ...entity,
    ...content,
    id: entity?.id,
    isCampaign: true,
    status: NOT_AVAILABLE,
    campaignTriggerType: entity?.triggerDetails?.type,
  };
};

export const useQueryCampaign = (campaignId, queryOptions = {}) => {
  const requestConfig = useRequestConfig();

  const fetch = ({ signal }) =>
    getAPI()
      .bonuses.getCampaign(campaignId, {
        ...requestConfig,
        signal,
        apiVersion: 'v3',
      })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          apiVersion: 'v3',
          campaignId,
          source: `SDK API bonuses.getCampaign`,
        })
      );

  return useQuery('campaign', fetch, {
    staleTime: 0,
    cacheTime: 0,
    enabled: !!campaignId,
    ...queryOptions,
  });
};

export const useQueryUnavailableCampaign = (campaignId, queryOptions = {}) => {
  const queryContent = useQueryBonusContent(campaignId, queryOptions);
  const queryCampaign = useQueryCampaign(campaignId, queryOptions);

  const data = getUnavailableEntity(
    queryCampaign.data?.campaign,
    queryContent?.data
  );

  return {
    isLoading: queryContent.isLoading || queryCampaign.isLoading,
    isError: queryContent.isError || queryCampaign.isError,
    error: queryContent.error || queryCampaign.error,
    data,
  };
};
